import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { INVOICE } from "@/constants/testIds";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FileText,
  Plus,
  Trash2,
  IndianRupee,
  Download,
  CreditCard,
} from "lucide-react";

const INVOICE_ELIGIBLE_STATUSES = [
  "Confirmed",
  "Payment Received",
  "Dispatched",
  "Delivered",
];

export default function InvoicePanel({ inquiry, onUpdated }) {
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [qty, setQty] = useState(1);
  const [juspay, setJuspay] = useState(inquiry?.juspay_ref || "");
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const lineItems = inquiry?.line_items || [];
  const eligible = INVOICE_ELIGIBLE_STATUSES.includes(inquiry?.status);

  useEffect(() => {
    setJuspay(inquiry?.juspay_ref || "");
  }, [inquiry?.id, inquiry?.juspay_ref]);

  useEffect(() => {
    if (!eligible) return;
    api
      .get("/products")
      .then(({ data }) => setProducts(data || []))
      .catch(() => {});
  }, [eligible]);

  const totals = useMemo(() => {
    const subtotal = lineItems.reduce(
      (s, li) => s + (li.qty || 0) * (li.unit_price || 0),
      0,
    );
    const cgstRate = inquiry?.cgst_rate ?? 2.5;
    const sgstRate = inquiry?.sgst_rate ?? 2.5;
    const cgst = (subtotal * cgstRate) / 100;
    const sgst = (subtotal * sgstRate) / 100;
    return {
      subtotal,
      cgstRate,
      sgstRate,
      cgst,
      sgst,
      grand: subtotal + cgst + sgst,
    };
  }, [lineItems, inquiry?.cgst_rate, inquiry?.sgst_rate]);

  if (!inquiry) return null;
  if (!eligible) {
    return (
      <div
        data-testid={INVOICE.panel}
        className="rounded-xl border border-dashed border-stone-300 p-3 text-xs text-stone-500 flex items-center gap-2"
      >
        <FileText className="w-4 h-4 shrink-0" />
        Invoice will unlock once the order reaches{" "}
        <span className="font-medium text-stone-700">Confirmed</span> or later.
      </div>
    );
  }

  const addItem = async () => {
    if (!selectedProductId) {
      toast.error("Pick a saree first");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/inquiries/${inquiry.id}/line-items`, {
        product_id: selectedProductId,
        qty: Math.max(1, Number(qty) || 1),
      });
      toast.success("Line item added");
      setSelectedProductId("");
      setQty(1);
      onUpdated?.();
    } catch {
      toast.error("Failed to add item");
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async (lineId) => {
    try {
      await api.delete(`/inquiries/${inquiry.id}/line-items/${lineId}`);
      toast.success("Removed");
      onUpdated?.();
    } catch {
      toast.error("Failed");
    }
  };

  const saveJuspay = async () => {
    try {
      await api.patch(`/inquiries/${inquiry.id}/invoice-meta`, {
        juspay_ref: juspay.trim() || null,
      });
      toast.success("Payment reference saved");
      onUpdated?.();
    } catch {
      toast.error("Failed");
    }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/inquiries/${inquiry.id}/invoice`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const invNo = `SMS-${inquiry.id.replace("inq_", "").slice(-8).toUpperCase()}`;
      a.download = `${invNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice downloaded");
    } catch {
      toast.error("Invoice download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      data-testid={INVOICE.panel}
      className="rounded-xl border border-terracotta/20 bg-white p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-terracotta/10 text-terracotta grid place-items-center">
            <FileText className="w-4 h-4" />
          </div>
          <div>
            <div className="overline text-terracotta">Order & Invoice</div>
            <div className="font-serif text-lg text-stone-900 leading-none mt-0.5">
              GST tax invoice
            </div>
          </div>
        </div>
        <Button
          data-testid={INVOICE.generateButton}
          onClick={download}
          disabled={downloading}
          className="bg-terracotta hover:bg-terracotta-dark rounded-full"
          size="sm"
        >
          <Download className="w-3.5 h-3.5 mr-1.5" />
          {downloading ? "Generating…" : "Generate Invoice"}
        </Button>
      </div>

      <div className="space-y-1.5 mb-3">
        {lineItems.length === 0 && (
          <div className="text-xs text-stone-500 italic py-2">
            No sarees on this order yet — add from the catalog below.
          </div>
        )}
        {lineItems.map((li) => (
          <div
            key={li.id}
            data-testid={INVOICE.itemRow(li.id)}
            className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-stone-50 border border-stone-100 text-sm"
          >
            <div className="min-w-0 flex-1">
              <div className="text-stone-800 truncate">{li.name}</div>
              <div className="text-[11px] text-stone-500">
                {li.fabric} · ₹{Number(li.unit_price).toLocaleString("en-IN")} ×{" "}
                {li.qty}
              </div>
            </div>
            <div className="text-terracotta font-medium">
              ₹{Number(li.qty * li.unit_price).toLocaleString("en-IN")}
            </div>
            <button
              data-testid={INVOICE.removeItem(li.id)}
              onClick={() => removeItem(li.id)}
              className="text-stone-400 hover:text-terracotta"
              title="Remove"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-[1fr_60px_auto] gap-2 mb-4">
        <Select value={selectedProductId} onValueChange={setSelectedProductId}>
          <SelectTrigger
            data-testid={INVOICE.addItemSelect}
            className="h-9 text-sm"
          >
            <SelectValue placeholder="Add a saree from catalog…" />
          </SelectTrigger>
          <SelectContent>
            {products.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name} — ₹{Number(p.price).toLocaleString("en-IN")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          data-testid={INVOICE.qtyInput}
          type="number"
          min={1}
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          className="h-9 text-center"
        />
        <Button
          data-testid={INVOICE.addItemButton}
          onClick={addItem}
          disabled={busy || !selectedProductId}
          variant="outline"
          size="sm"
          className="h-9"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add
        </Button>
      </div>

      {lineItems.length > 0 && (
        <div className="rounded-lg bg-stone-50/60 border border-stone-100 p-3 mb-4 space-y-1 text-sm">
          <div className="flex justify-between text-stone-600">
            <span>Subtotal</span>
            <span>₹{totals.subtotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>CGST @ {totals.cgstRate}%</span>
            <span>₹{totals.cgst.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>SGST @ {totals.sgstRate}%</span>
            <span>₹{totals.sgst.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between pt-1.5 mt-1.5 border-t border-stone-200 font-serif text-lg text-terracotta">
            <span>Grand total</span>
            <span>
              <IndianRupee className="inline w-4 h-4 -mt-0.5" />
              {totals.grand.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      )}

      <div>
        <label className="overline flex items-center gap-1.5 mb-1.5">
          <CreditCard className="w-3 h-3 text-terracotta" />
          Juspay payment reference
        </label>
        <div className="flex items-center gap-2">
          <Input
            data-testid={INVOICE.juspayInput}
            placeholder="e.g. jusp_prod_9d7f88e12b03"
            value={juspay}
            onChange={(e) => setJuspay(e.target.value)}
            className="h-9 text-sm"
          />
          <Button
            data-testid={INVOICE.juspaySave}
            onClick={saveJuspay}
            variant="outline"
            size="sm"
            className="h-9"
          >
            Save
          </Button>
        </div>
        {inquiry.juspay_ref && (
          <div className="text-[11px] text-emerald-700 mt-1">
            ✓ Payment recorded — will appear as a highlighted block on the invoice.
          </div>
        )}
      </div>
    </div>
  );
}
